import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../services/api';
import { createApiUrlWithPrefix, createApiUrl } from '../config/api';
import { toast } from 'react-hot-toast';
import { X, UserPlus, Mail, Phone, Calendar, User, Lock, Upload, Camera, FileText, Eye, EyeOff } from 'lucide-react';
import Modal, { ModalSection, ModalFooterBar } from './ui/Modal';

function UserCreationModal({ isOpen, onClose, role, onUserCreated, initialData, mode, onSubmit }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    subscriberId: initialData?.id || null,
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    phoneNumber: initialData?.phoneNumber || '',
    birthDate: initialData?.birthDate ? new Date(initialData.birthDate).toISOString().split('T')[0] : '',
    address: initialData?.address || '',
    bloodType: initialData?.bloodType || '',
    level: '',
    role: (mode === 'approve' && !role) ? '' : (role || 'Rider')
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(prev => {
        // If the ID hasn't changed, keep the current role and level (user might have started typing)
        const isSameSubscriber = initialData.id && prev.subscriberId === initialData.id;
        
        return {
          ...prev,
          subscriberId: initialData.id,
          firstName: initialData.firstName || '',
          lastName: initialData.lastName || '',
          email: initialData.email || '',
          phoneNumber: initialData.phoneNumber || '',
          birthDate: initialData.birthDate ? new Date(initialData.birthDate).toISOString().split('T')[0] : '',
          address: initialData.address || '',
          bloodType: initialData.bloodType || '',
          level: isSameSubscriber ? prev.level : '',
          role: isSameSubscriber ? prev.role : ((mode === 'approve' && !role) ? (prev.role || '') : (role || 'Rider'))
        };
      });
    }
  }, [initialData, role, mode]);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [pjDocumentFile, setPjDocumentFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [useCustomPassword, setUseCustomPassword] = useState(false);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'photo') {
      setPhotoFile(file);
    } else if (type === 'pjDocument') {
      setPjDocumentFile(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // If mode is "approve", use the approve endpoint
      if (mode === 'approve') {
        // Validate role is selected
        if (!formData.role) {
          toast.error('Please select a user role');
          setLoading(false);
          return;
        }

        // Use onSubmit callback if provided (for custom approval logic)
        if (onSubmit) {
          const approveData = {
            role: getRoleValue(formData.role),
            level: formData.level ? parseInt(formData.level) : null,
            generateCredentials: true,
            mustChangePassword: true,
            password: useCustomPassword ? customPassword : undefined,
            notes: ''
          };
          await onSubmit(approveData);
          if (onUserCreated) {
            onUserCreated();
          }
          onClose();
          return;
        }

        // Otherwise, create user directly and let parent handle subscriber conversion
        const apiData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
          address: formData.address,
          bloodType: formData.bloodType,
          role: getRoleValue(formData.role),
          level: formData.level ? parseInt(formData.level) : null,
          generateCredentials: true,
          mustChangePassword: true,
          password: useCustomPassword ? customPassword : undefined
        };

        // Create user
        const createResponse = await axios.post(
          createApiUrlWithPrefix('users/create'),
          apiData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const newUser = createResponse.data.user || createResponse.data;

        // Upload photo if provided
        if (photoFile) {
          const photoFormData = new FormData();
          photoFormData.append('photo', photoFile);
          // Do NOT set Content-Type manually - let axios set it with boundary
          await axios.post(createApiUrlWithPrefix(`users/upload-photo/${newUser.id}`), photoFormData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
        }

        // Upload PJ document if provided
        if (pjDocumentFile) {
          const documentFormData = new FormData();
          documentFormData.append('document', pjDocumentFile);
          // Do NOT set Content-Type manually - let axios set it with boundary
          await axios.post(createApiUrlWithPrefix(`users/upload-pj-document/${newUser.id}`), documentFormData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
        }

        // Call onUserCreated callback
        if (onUserCreated) {
          await onUserCreated(newUser);
        }

        onClose();
        return;
      }

      // Convert role string to proper format for API
      const apiData = {
        ...formData,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
        role: getRoleValue(role), // Convert to number
        level: formData.level ? parseInt(formData.level) : null, // Convert to number
        generateCredentials: true,
        mustChangePassword: true,
        password: useCustomPassword ? customPassword : undefined
      };

      // Debug logging
      console.log('Form data being sent:', {
        birthDate: formData.birthDate,
        birthDateType: typeof formData.birthDate,
        fullFormData: formData,
        apiData: apiData
      });

      console.log('Role being sent:', role);
      console.log('Role value being sent:', getRoleValue(role));

      const response = await axios.post(createApiUrlWithPrefix('users'), apiData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const userId = response.data.user.id;

      // Upload photo if provided
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        // Do NOT set Content-Type manually - let axios set it with boundary
        await axios.post(createApiUrlWithPrefix(`users/upload-photo/${userId}`), photoFormData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      // Upload PJ document if provided
      if (pjDocumentFile) {
        const documentFormData = new FormData();
        documentFormData.append('document', pjDocumentFile);
        // Do NOT set Content-Type manually - let axios set it with boundary
        await axios.post(createApiUrlWithPrefix(`users/upload-pj-document/${userId}`), documentFormData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      toast.success(`${getRoleDisplayName(role)} created successfully!`);
      // Pass the created user to the callback
      const createdUser = response.data.user || { id: userId };
      if (onUserCreated) {
        onUserCreated(createdUser);
      }
      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to create user:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);

      const errorMessage = error.response?.data?.Message || error.response?.data?.message || error.message || 'Failed to create user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      birthDate: '',
      address: '',
      bloodType: '',
      level: '',
      role: role
    });
    setPhotoFile(null);
    setPjDocumentFile(null);
    setCustomPassword('');
    setUseCustomPassword(false);
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'Admin': return 'Personnel';
      case 'Coach': return 'Coach';
      case 'Rider': return 'Rider';
      case 'Owner': return 'Owner';
      case 'Temporary': return 'Temporary User';
      default: return 'User';
    }
  };

  const getRoleValue = (role) => {
    switch (role) {
      case 'Admin': return 3; // Admin
      case 'Coach': return 2; // Coach
      case 'Rider': return 0; // Rider
      case 'Owner': return 1; // Owner
      case 'Temporary': return 5; // Temporary
      default: return 0;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'approve' ? 'Approve Subscriber' : `Add New ${getRoleDisplayName(role)}`}
      subtitle={t('common.createNewAccount')}
      icon={UserPlus}
      variant="blue"
      size="lg"
      dir={isRTL ? 'rtl' : 'ltr'}
      bodyClassName="p-0"
      footer={
        <ModalFooterBar>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="user-create-form"
            disabled={loading}
            className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {mode === 'approve' ? 'Approving…' : 'Creating…'}
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                {mode === 'approve' ? 'Approve Subscriber' : t('common.createUser')}
              </>
            )}
          </button>
        </ModalFooterBar>
      }
    >
        <form id="user-create-form" onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-6">
          {/* Personal Information Section */}
          <ModalSection title={t('common.personalInformation')} icon={User}>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.firstName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  disabled={mode === 'approve'}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  placeholder={t('users.enterFirstName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.lastName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  disabled={mode === 'approve'}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  placeholder={t('users.enterLastName')}
                />
              </div>
            </div>

            {/* Account Creation Section */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Login Account Creation
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                A login account will be created for this user with the email and password below.
              </p>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.emailAddress')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={mode === 'approve'}
                    className={`w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    placeholder={t('users.enterEmailForLogin')}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.phoneNumber')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    disabled={mode === 'approve'}
                    className={`w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    placeholder={t('users.enterPhoneNumber')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.birthDate')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleInputChange}
                    required
                    disabled={mode === 'approve'}
                    className={`w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.address')}
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={mode === 'approve'}
                  placeholder={t('users.enterAddress')}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.bloodType')}
                </label>
                <select
                  name="bloodType"
                  value={formData.bloodType}
                  onChange={handleInputChange}
                  disabled={mode === 'approve'}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${mode === 'approve' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                >
                  <option value="">Select blood type</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
            </div>

            {/* Role Selection - Show only in approve mode */}
            {mode === 'approve' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select user role</option>
                  <option value="Admin">Admin (Personnel)</option>
                  <option value="Coach">Coach</option>
                  <option value="Rider">Rider</option>
                  <option value="Owner">Owner</option>
                  <option value="Temporary">Temporary User</option>
                </select>
              </div>
            )}

            {/* Rider Level Field - Show for Riders, Coaches and Temporary users */}
            {(formData.role === 'Rider' || formData.role === 'Coach' || formData.role === 'Temporary' || (mode !== 'approve' && (role === 'Rider' || role === 'Coach' || role === 'Temporary'))) && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.riderLevel')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select rider level</option>
                  <option value="0">Beginner</option>
                  <option value="1">Intermediate</option>
                  <option value="2">Professional</option>
                </select>
              </div>
            )}
          </ModalSection>

          {/* File Upload Section */}
          <ModalSection title={t('common.documentsPhotos')} icon={Upload} iconClassName="text-blue-600">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Camera className="w-4 h-4 inline mr-1" />
                  {t('common.profilePhoto')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer flex flex-col items-center">
                    <Camera className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload photo</span>
                    <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</span>
                  </label>
                  {photoFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 flex items-center">
                        <Camera className="w-4 h-4 mr-1" />
                        Selected: {photoFile.name}
                      </p>
                    </div>
                  )}
                  {!photoFile && initialData?.photoPath && (
                    <div className="mt-2 text-center p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-700 mb-1 font-medium">Existing Photo:</p>
                      <img
                        src={createApiUrl(initialData.photoPath)}
                        alt="Existing Profile"
                        className="w-16 h-16 object-cover rounded-lg border border-blue-200 mx-auto"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  {t('common.pjDocument')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => handleFileChange(e, 'pjDocument')}
                    className="hidden"
                    id="pj-upload"
                  />
                  <label htmlFor="pj-upload" className="cursor-pointer flex flex-col items-center">
                    <FileText className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload medical certificate</span>
                    <span className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX up to 10MB</span>
                  </label>
                  {pjDocumentFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 flex items-center">
                        <FileText className="w-4 h-4 mr-1" />
                        {t('common.selected')}: {pjDocumentFile.name}
                      </p>
                    </div>
                  )}
                  {!pjDocumentFile && initialData?.pjDocumentPath && (
                    <div className="mt-2 text-center p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-700 mb-1 font-medium">Existing Medical Certificate:</p>
                      <a
                        href={createApiUrl(initialData.pjDocumentPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-blue-600 underline flex flex-col items-center"
                      >
                        <FileText className="w-8 h-8 mb-1" />
                        View Medical Certificate
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ModalSection>

          {/* Account Credentials Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Lock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  Account Credentials
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="useCustomPassword"
                      checked={useCustomPassword}
                      onChange={(e) => setUseCustomPassword(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="useCustomPassword" className="text-sm font-medium text-blue-800">
                      {t('common.useCustomPassword')}
                    </label>
                  </div>

                  {useCustomPassword ? (
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={customPassword}
                        onChange={(e) => setCustomPassword(e.target.value)}
                        placeholder={t('users.enterCustomPassword')}
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700">
                        <strong>Auto-generated credentials:</strong><br />
                        Email: <code className="bg-blue-100 px-1 rounded">{formData.email || t('common.enterEmailAbove')}</code><br />
                        Default password: <code className="bg-blue-100 px-1 rounded">Club2024</code><br />
                        <span className="text-blue-600">User will be required to change password on first login.</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </form>
    </Modal>
  );
}

export default UserCreationModal;
